import {
  formatCompactAmount,
  formatCompactAmountForCurrency,
  formatCompactNumber,
  formatDateLong,
  formatDayMonth,
  formatDuration,
  formatPercentage,
} from "@/lib/format-amount";
import { toHumanReadableAmount } from "@/lib/string";
import { CompanyDTO, displayableLegalForm } from "@shaarp/common";

type Translate = (key: string, options?: Record<string, unknown>) => string;

export function pickRevenueValue(company: CompanyDTO): string | null {
  const finances = company.business?.finances ?? [];
  const latestWithSales = [...finances]
    .filter((f) => f.sales != null && f.sales > 0)
    .sort((a, b) => b.year - a.year)[0];

  if (latestWithSales?.sales != null) {
    return formatCompactAmount(latestWithSales.sales);
  }
  if (company.estimatedRevenue?.range) {
    return `~ ${company.estimatedRevenue.range}`;
  }
  return null;
}

export function pickEmployeesValue(company: CompanyDTO): string | null {
  const exact = company.employeesCount?.exact;
  if (exact != null && exact > 0) return String(exact);

  // The bounds of a bracket (INSEE's, or the web profile's), not a count.
  const min = company.employeesCount?.min;
  const max = company.employeesCount?.max;
  if (min != null && max != null && max > 0) return `~ ${min}–${max}`;

  const range = company.employeesCount?.estimatedRange;
  if (range) return `~ ${range}`;

  return null;
}

export function pickFoundedYear(company: CompanyDTO): string | null {
  const date = company.business?.creationDate;
  if (!date) return null;
  const year = new Date(date).getFullYear();
  return Number.isFinite(year) ? String(year) : null;
}

export function pickLocation(company: CompanyDTO): string | null {
  const city = company.location?.address?.city;
  const country = company.location?.address?.country;
  return [city, country].filter(Boolean).join(", ") || null;
}

export type WebTrafficRow = { label: string; value: string };

export function pickWebTrafficRows(
  company: CompanyDTO,
  t: Translate,
): WebTrafficRow[] {
  const traffic = company.webTraffic;
  if (!traffic) return [];

  const rows: WebTrafficRow[] = [];
  if (traffic.monthlyVisits != null && traffic.monthlyVisits > 0) {
    rows.push({
      label: t("company.insights.digital.monthlyVisits"),
      value: formatCompactNumber(traffic.monthlyVisits),
    });
  }
  if (traffic.bounceRate != null && traffic.bounceRate > 0) {
    rows.push({
      label: t("company.insights.digital.bounceRate"),
      value: formatPercentage(traffic.bounceRate),
    });
  }
  if (traffic.pagesPerVisit != null && traffic.pagesPerVisit > 0) {
    rows.push({
      label: t("company.insights.digital.pagesPerVisit"),
      value: traffic.pagesPerVisit.toFixed(1),
    });
  }
  if (
    traffic.avgVisitDurationSeconds != null &&
    traffic.avgVisitDurationSeconds > 0
  ) {
    rows.push({
      label: t("company.insights.digital.avgVisitDuration"),
      value: formatDuration(traffic.avgVisitDurationSeconds),
    });
  }
  return rows;
}

export type LegalInfoRow = {
  key: string;
  label: string;
  value: string;
  copyValue?: string;
};

/** "21 December 2018 · Montpellier court"; null when neither is known. */
export function formatRegistration(
  business: CompanyDTO["business"],
  t: Translate,
  language: string,
): string | null {
  const parts = [
    business.registeredAt
      ? formatDateLong(business.registeredAt, language)
      : null,
    business.registryCourt
      ? t("company.insights.identity.registryCourt", {
          court: business.registryCourt,
        })
      : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : null;
}

/**
 * "December 31 · next closing on December 31, 2026"; null when neither is
 * known. The day and month come from the next closing date, in the user's
 * language: the registry's own `fiscalYearEnd` is French text ("31
 * Décembre"), shown as is only to a French reader.
 */
export function formatFiscalYear(
  business: CompanyDTO["business"],
  t: Translate,
  language: string,
): string | null {
  const yearEnd = business.nextClosingDate
    ? formatDayMonth(business.nextClosingDate, language)
    : language === "fr"
      ? business.fiscalYearEnd
      : null;
  const parts = [
    yearEnd,
    business.nextClosingDate
      ? t("company.insights.identity.nextClosing", {
          date: formatDateLong(business.nextClosingDate, language),
        })
      : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : null;
}

export function pickLegalInfoRows(
  company: CompanyDTO,
  t: Translate,
  language: string,
): LegalInfoRow[] {
  const business = company.business;
  if (!business) return [];

  const rows: LegalInfoRow[] = [];

  const legalForm = displayableLegalForm(business.legalForm);
  if (legalForm) {
    rows.push({
      key: "legalForm",
      label: t("company.insights.legalForm"),
      value: legalForm,
    });
  }

  if (business.capital != null && business.capital > 0) {
    rows.push({
      key: "capital",
      label: t("company.insights.capital"),
      value: toHumanReadableAmount(business.capital),
    });
  }

  if (business.enterpriseCategory) {
    rows.push({
      key: "enterpriseCategory",
      label: t("company.insights.enterpriseCategory"),
      value: business.enterpriseCategory,
    });
  }

  if (business.sirenFormatted || business.siren) {
    const display = business.sirenFormatted ?? business.siren!;
    rows.push({
      key: "siren",
      label: t("company.insights.identity.siren"),
      value: display,
      copyValue: business.siren ?? display,
    });
  }

  if (business.siretFormatted || business.siret) {
    const display = business.siretFormatted ?? business.siret!;
    rows.push({
      key: "siret",
      label: t("company.insights.identity.siret"),
      value: display,
      copyValue: business.siret ?? display,
    });
  }

  if (business.nafCode && business.nafLabel) {
    rows.push({
      key: "naf",
      label: t("company.insights.identity.nafCode"),
      value: `${business.nafCode} – ${business.nafLabel}`,
    });
  }

  const registration = formatRegistration(business, t, language);
  if (registration) {
    rows.push({
      key: "registration",
      label: t("company.insights.identity.registration"),
      value: registration,
    });
  }

  const fiscalYear = formatFiscalYear(business, t, language);
  if (fiscalYear) {
    rows.push({
      key: "fiscalYear",
      label: t("company.insights.identity.fiscalYear"),
      value: fiscalYear,
    });
  }

  if (business.hasConfidentialAccounts) {
    rows.push({
      key: "accounts",
      label: t("company.insights.identity.accounts"),
      value: t("company.insights.identity.confidentialAccounts"),
    });
  }

  return rows;
}

export type FundingSummary = {
  totalFundingFormatted: string | null;
  latestRoundLabel: string | null;
  totalRoundsCount: number | null;
};

export function pickFundingSummary(company: CompanyDTO): FundingSummary | null {
  const funding = company.funding;
  const hasTotal = funding?.totalFunding != null && funding.totalFunding > 0;
  const hasLatest = !!funding?.latestRoundType;
  const hasRoundsCount =
    funding?.totalRoundsCount != null && funding.totalRoundsCount > 0;

  if (!hasTotal && !hasLatest && !hasRoundsCount) return null;

  const totalFundingFormatted = hasTotal
    ? formatCompactAmountForCurrency(
        funding!.totalFunding!,
        funding!.latestRoundAmountCurrency,
      )
    : null;

  let latestRoundLabel: string | null = null;
  if (hasLatest) {
    const parts: string[] = [funding!.latestRoundType!];
    if (funding!.latestRoundDate) {
      const year = new Date(funding!.latestRoundDate).getFullYear();
      if (Number.isFinite(year)) parts.push(`(${year})`);
    }
    latestRoundLabel = parts.join(" ");
  }

  return {
    totalFundingFormatted,
    latestRoundLabel,
    totalRoundsCount: hasRoundsCount ? funding!.totalRoundsCount : null,
  };
}
